<template>
  <div class="dialog-container dialog-light">
    <!-- select or manage 2FA -->
    <div class="dialog dialog-md" ref="accountSecurityDialog" v-if="!this.showProtocol">
      <!-- header -->
      <ax-dialog-header
        v-if="!this.firstSetup"
        :text="'Account Security'"
        :type="'accountSecurity'"
        :description="'To remove your current 2FA, click on the red bin icon. To add 2FA, click on the plus button next to each 2FA method.'"
      ></ax-dialog-header>
      <ax-dialog-header
        v-if="this.firstSetup"
        :text="'Enable Two Factor Authentication'"
        :type="'accountSecurity'"
        :description="'If you would like to add this later, it can be added via the Account Security page.'"
      ></ax-dialog-header>
      <!-- Options -->
      <div class="dialog-body" id="accountSecurityOptions">
        <div class="options-group pb-4">
          <!-- phone -->
          <select-item
            :text="this.mobileSelectText"
            :description="this.phoneOptionDescription"
            :canSelect=false
            :value=this.phone
            :hideIcon=false
            :type="'phone'"
            @select="selectPhoneProtocol"
          ></select-item>
          <!-- email -->
          <select-item
            :text="this.emailSelectText"
            :description="this.emailOptionDescription"
            :canSelect=false
            :value=this.email
            :hideIcon=false
            :type="'email'"
            @select="selectEmailProtocol"
          ></select-item>
        </div>
        <div class="mt-5">
          <k-button
            :class="{'btn btn-lg btn-outline-primary w-100' : !this.firstSetup, 'btn btn-lg btn-secondary w-100' : this.firstSetup}"
            type="button"
            @click="this.clickNext2fa"
          >{{ this.button2FaText }}</k-button>
        </div>
      </div>
    </div>
    <!-- Add or remove 2fa protocol -->
    <div class="dialog dialog-md" ref="addVerificationDialog" v-if="this.showProtocol">
      <ax-dialog-header
        v-if="this.addNew2FaProtocol && this.typeOfAddProtocol === 'phone'"
        :text="'Add verification'"
        :type="'accountSecurity'"
        :description="'Please enter your mobile number. Once entered please enter the code that will be sent to the number.'"
      ></ax-dialog-header>
      <ax-dialog-header
        v-if="this.addNew2FaProtocol && this.typeOfAddProtocol === 'email'"
        :text="'Add verification'"
        :type="'accountSecurity'"
        :description="'Please enter your email address. Once entered please enter the code that will be sent to the email address.'"
      ></ax-dialog-header>
      <ax-dialog-header
        v-if="!this.addNew2FaProtocol"
        :text="'Remove verification'"
        :type="'accountSecurity'"
        :description="this.removeDescription"
      ></ax-dialog-header>
      <div class="dialog-body" id="addVerificationOptions">
        <!-- Remove Verification method -->
        <div class="mb-4" v-if="!this.addNew2FaProtocol">
          <h2>Verification method</h2>
          <div class="row">
            <k-radio
              class="col-6"
              :value="'phone'"
              :checked="this.removeVerificationMethod === 'phone'"
              @change="handleRemoveVerWithSMSChange"
              :label="'SMS'"
              :disabled="!this.phone"
            />
            <k-radio
              class="col-6"
              :value="'email'"
              :checked="this.removeVerificationMethod === 'email'"
              @change="handleRemoveVerWithEmailChange"
              :label="'Email'"
              :disabled="!this.email"
            />
          </div>
        </div>
        <div class="options-group">
          <div ref="verificationOptions">
            <!-- add 2fa protocol -->
            <add-2fa-protocol
              v-if="this.addNew2FaProtocol"
              :protocol="this.typeOfAddProtocol"
              ref="add2FA"
            ></add-2fa-protocol>
            <!-- remove 2fa protocol -->
            <get-2fa-code
              v-if="!this.addNew2FaProtocol"
              :add2FA="this.addNew2FaProtocol"
              :method="this.typeOfAddProtocol"
              :verificationMethod="this.removeVerificationMethod"
            ></get-2fa-code>
          </div>
          <div class="mt-5" v-if="this.addNew2FaProtocol">
            <k-button
              class="btn btn-lg btn-secondary w-100 mb-2"
              type="button"
              v-if="this.addNew2FaProtocol"
              @click="submitCode"
            >{{ this.addRemoveVerificationBttText }}</k-button>
            <k-button
              class="btn btn-lg btn-secondary w-100 mb-2"
              type="button"
              v-else
            >{{ this.addRemoveVerificationBttText }}</k-button>
            <k-button
              class="btn btn-lg btn-outline-primary w-100"
              type="button"
              @click="backToProtocolOptions"
            >Back</k-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SelectItem from "../SelectItem.vue";
import Add2FaProtocol from "../Add2FaProtocol.vue";
import Get2FaCode from "../Get2FaCode.vue";
import AuthService from "../../services/auth-service";
import store from "../../store";

export default({
  name: "account-security",
  props: {
    firstSetup: Boolean
  },
  components: {
    "select-item": SelectItem,
    "add-2fa-protocol": Add2FaProtocol,
    "get-2fa-code": Get2FaCode
  },
  computed: {
    button2FaText: function() {
      return this.firstSetup ? "Next" : "Close";
    },
    phoneOptionDescription: function() {
      return this.phone ? `Current number: ${this.phone}` : "Click here to add a mobile";
    },
    emailOptionDescription: function() {
      return this.email ? `Current email: ${this.email}` : "Click here to add email address";
    },
    addRemoveVerificationBttText: function() {
      return this.addNew2FaProtocol ? "Add verification" : "Submit";
    },
    mobileSelectText: function() {
      return !this.firstSetup || this.phone ? "Verify with mobile" : "Add mobile 2FA"
    },
    emailSelectText: function() {
      return !this.firstSetup || this.email ? "Verify with email" : "Add email 2FA"
    }
  },
  data() {
    return {
      phone: "",
      email: "",
      showProtocol: false,
      typeOfAddProtocol: "email",
      addNew2FaProtocol: false,
      removeVerificationMethod: "",
      removeDescriptionKey: ""
    }
  },
  mounted() {
    this.removeVerificationMethod = this.phone ? "phone" : "email";
    this.getTwoFactorMethods();
  },
  methods: {
    getTwoFactorMethods() {
      AuthService.getTwoFactorMethodsByID(store.getters.impersonateUserID || store.getters.userID, store.getters.twoFactorGUID)
        .then((response) => {
          this.phone = response.phone;
          this.email = response.email;
      }).catch(this.showAlert);
    },
    selectPhoneProtocol() {
      this.showProtocol = true;
      this.typeOfAddProtocol = "phone";
      if (this.phone) {
        this.addNew2FaProtocol = false;
        this.removeDescription = "Please select which verification method you would like to use to remove ‘Verify with mobile’.";
      }
      else {
        this.addNew2FaProtocol = true;
      }
      this.handleRemoveVerWithSMSChange();
    },
    selectEmailProtocol() {
      this.showProtocol = true;
      this.typeOfAddProtocol = "email";
      if (this.email) {
        this.addNew2FaProtocol = false;
        this.removeDescription = "Please select which verification method you would like to use to remove ‘Verify with email.’";
      }
      else {
        this.addNew2FaProtocol = true;
      }
      this.handleRemoveVerWithEmailChange();
    },
    backToProtocolOptions() {
      this.showProtocol = false;
    },
    handleRemoveVerWithSMSChange() {
      this.removeVerificationMethod = "phone";
    },
    handleRemoveVerWithEmailChange() {
      this.removeVerificationMethod = "email";
    },
    submitCode() {
      this.$refs.add2FA.submitCode();
    },
    click2fa() {
      this.showProtocol = false;
      this.$emit("click2Fa");
      this.getTwoFactorMethods();
    },
    clickNext2fa() {
      if (this.firstSetup) {
        this.$emit("clickNext2Fa");
      } else {
        this.click2fa();
      }
    }
  }
})
</script>
